import { renderProfileHeader } from './profileHelper';
import { FC, useEffect, useMemo, useState } from 'react';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import { UserPresentationFactory } from '../../strategies/userPresentation/userPresentationFactory';
import { UserPresentationStrategy } from '../../strategies/userPresentation/userPresentationStrategy.interface';
import { isUndefined } from 'lodash';
import { AuthTokenResponse } from '../../service/service.type';
import { isBase64Image, validateAndDecodeAuthToken } from '../../utils/helper';
import { UpdateUserResponse, UserType } from '@inplay/models';
import Loading from '../../assets/loading/loading';
import EditProfile from './editProfile';
import ViewProfile from './viewProfile';
import { useUpdateProfile } from '../../hooks/useUserData';
import { useLocation, useNavigate } from 'react-router-dom';
import SuccessToast from '../../component/successModal/successModal';
import {
  apiStatusCode,
  StatusResponse,
  SuccessMessage,
} from '../../utils/helper.type';

const Profile: FC = () => {
  const location = useLocation();
  const { userDetail, userDetailLoading, updateUserDetail } =
    useUserDetailContext();
  const [showEditProfile, setShowEditProfile] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [userPresentationStrategy, setUserPresentationStrategy] =
    useState<UserPresentationStrategy>();
  const [viewProfileDetail, setViewProfileDetail] = useState<
    Record<string, string>
  >({});
  const [showChangePasswordSuccessToast, setShowChangePasswordSuccessToast] =
    useState<boolean>(false);
  const [validate, setValidate] = useState<() => boolean>(() => () => true);

  const { updateProfile, updateProfileLoading } = useUpdateProfile();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.showChangePasswordSuccessToast) {
      setShowChangePasswordSuccessToast(true);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const userType: UserType | undefined = useMemo(() => {
    const validAuthToken: AuthTokenResponse | null =
      validateAndDecodeAuthToken();

    return validAuthToken?.type as UserType | undefined;
  }, []);

  useEffect(() => {
    if (!isUndefined(userType)) {
      setUserPresentationStrategy(
        UserPresentationFactory.getPresentationStrategy(userType, userDetail!),
      );
    }
  }, [userType, userDetail]);

  useEffect(() => {
    if (!isUndefined(userPresentationStrategy) && !isUndefined(userDetail)) {
      const userProfileDetail: Record<string, string> =
        userPresentationStrategy.getViewableFieldsForProfile(userDetail);
      console.log('user profile detail', userProfileDetail);
      setViewProfileDetail(userProfileDetail);
    }
  }, [userDetail, userPresentationStrategy]);

  const handleEdit = () => {
    setShowEditProfile(true);
  };

  const handleCancel = () => {
    setShowEditProfile(false);
  };

  const handleProfileSave = async () => {
    if (updateProfileLoading) {
      console.log('[Profile] Already in progress... skipping this request');
      return;
    }
    if (!validate) {
      console.warn('Form validation failed');
      return;
    }

    try {
      console.log(`[ENTRY][UpdateProfile]`);

      const payload: {
        firstName: string;
        lastName: string;
        email: string;
        mobileNumber?: number;
        signature?: string;
        profilePhoto?: string;
      } = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        ...(formValues.mobile?.number && {
          mobileNumber: Number(formValues.mobile.number),
        }),
        ...(isBase64Image(formValues.signature) && {
          signature: formValues.signature,
        }),
        ...((isBase64Image(formValues.profilePictureBase64) ||
          isBase64Image(formValues.profilePictureUrl)) && {
          profilePhoto:
            formValues.profilePictureBase64 || formValues.profilePictureUrl,
        }),
      };

      const updateProfileResponse: UpdateUserResponse | undefined =
        await updateProfile(payload);

      updateUserDetail({
        ...userDetail,
        ...formValues,
        subOrganizations: userDetail?.subOrganizations,
        mobileNumber: formValues.mobile?.number,
        organization: userDetail?.organization,
        venue: userDetail?.venue,
        ...(formValues.signature && { signature: formValues.signature }),
        ...((formValues.profilePictureBase64 ||
          formValues.profilePictureUrl) && {
          profilePhoto:
            formValues.profilePictureBase64 || formValues.profilePictureUrl,
        }),
      });

      console.log(`[UpdateUserProfile] close...`);

      if (!isUndefined(updateProfileResponse)) {
        const updateProfileResponseWithStatus: StatusResponse = {
          statusCode: apiStatusCode.SUCCESS,
          data: updateProfileResponse,
        };

        console.log(
          `[updateProfileResponseWithStatus]`,
          updateProfileResponseWithStatus,
        );

        if (
          updateProfileResponseWithStatus?.statusCode === apiStatusCode.SUCCESS
        ) {
          setShowEditProfile(false);
          console.log(`[updateProfileResponseWithStatus] close modal`);
        }
      }
    } catch (error: unknown) {
      console.warn(`[UpdateUserProfile]`, { error });
      throw error;
    } finally {
      console.warn(`[UpdateUserProfile] finally block...`);
    }

    console.log('Form values on Save:', formValues);
  };

  return (
    <>
      <div className="header-content">
        <div
          className="table-content relative flex flex-col overflow-hidden py-2 px-8"
          style={{ boxShadow: '0px 0px 25px #2F41A51F' }}
        >
          {showChangePasswordSuccessToast && (
            <SuccessToast
              message={SuccessMessage.CHANGE_PASSWORD_SUCCESS}
              onClose={() => setShowChangePasswordSuccessToast(false)}
              className="bottom-15 bg-white right-10"
            />
          )}
          {userDetailLoading ? (
            <div className="flex justify-center items-center h-full">
              <Loading />
            </div>
          ) : (
            <>
              {renderProfileHeader(
                handleEdit,
                showEditProfile,
                handleCancel,
                handleProfileSave,
                updateProfileLoading,
              )}

              {showEditProfile ? (
                <EditProfile
                  formValues={formValues}
                  setFormValues={setFormValues}
                  setValidator={setValidate}
                />
              ) : (
                <ViewProfile
                  userDetail={userDetail}
                  viewProfileDetail={viewProfileDetail}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
export default Profile;
